import React, { MouseEventHandler } from 'react';
import { useRouter } from "@montana/containers/router";
import { useTracking } from '@montana/containers/tracking';
import { TrackingTypes } from './tracking.helper';

/*
 * Based on original code, found here:
 * https://github.com/AlephSF/react-router-hijack-a/blob/master/src/index.js
 *
 * Sadly, that implementation does not contain TS-definitions (and none are available from Definitely Typed),
 * but more importantly: the original code wraps everything in a DIV-tag indiscriminately. We don't really
 * want that, so this is the way it goes instead.
*/


interface ILocationObject {
    href: string;
    protocol: string;
    host: string;
    hostname: string;
    port: string;
    pathname: string;
    search: string;
    hash: string;
}


type ILinkHijackChildren = {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
}


interface ILinkHijackProps {
    children: JSX.Element | JSX.Element[] | (({ onClick }: ILinkHijackChildren) => JSX.Element);
    protocols?: string[];
    hostnames?: string[];
}


const getLocation = (href: string): ILocationObject | void => {
    const re = /^(https?:)?\/\/(([^:/?#]*)(?::([0-9]+))?)([/]?[^?#]*)(\?[^#]*|)(#.*|)$/;
    const match = href.match(re);

    return match
        ? {
            href,
            protocol: match[1],
            host: match[2],
            hostname: match[3],
            port: match[4],
            pathname: match[5],
            search: match[6],
            hash: match[7],
        }
        : undefined;
};


export const LinkHijack: React.FC<ILinkHijackProps> = React.memo((props: ILinkHijackProps): JSX.Element => {

    const {
        protocols = ['mailto', 'tel'],
        hostnames = [],
        children,
    } = props;

    const { push: pushHistoryItem } = useRouter();
    const { trackCustomEvent } = useTracking();


    const handleWithRouter = (url: string, event: React.MouseEvent<HTMLElement>): void => {
        // if it is an explicit non-http protocol, we want the browser to handle it natively
        // using history.push will do bad things
        const nonHttpProtocol = protocols.some((protocol): boolean => url.startsWith(`${protocol}:`));
        if (nonHttpProtocol) {
            return;
        }

        let destination = url;

        // if it's http or protocol-relative, we need to check further
        // we want to use react router if it's sending us to the same domain
        const re = new RegExp('^(https?:)?//');
        const isHTTP = re.test(url);

        if (isHTTP) {
            const parsedURL = getLocation(url);

            if (parsedURL) {
                const {
                    hostname,
                    pathname,
                    search,
                    hash,
                } = parsedURL;

                const sameSite = hostname === window.location.hostname || hostnames.indexOf(hostname) !== -1;
                if (sameSite) {
                    destination = pathname;
                    if (search) { destination += search; }
                    if (hash) { destination += hash; }
                } else {
                    // if it's an external http url, we let the browser handle it natively
                    return;
                }
            }
        }

        // in all other cases (query string, hash fragment, relative URL)
        // we directly push the url and React Router will handle it correctly
        pushHistoryItem(destination);
        event.preventDefault();
    };


    const onClick = (event: React.MouseEvent<HTMLElement>): void => {
        // if the user is trying to right click or open the link in a new tab or window
        // we do not want to interfere
        const { ctrlKey, shiftKey, metaKey } = event;
        if (ctrlKey || shiftKey || metaKey) {
            return;
        }

        // when Hijack is clicked, we look at the target and each of the target's parents
        // if an <a> tag was clicked, we process it further
        const self = event.currentTarget;

        for (let el = event.target as HTMLElement | null; el !== self; el = el && el.parentElement) {
            if (!el) {
                break;
            }

            if (el.nodeName === 'A') {
                const target = el.getAttribute('target');
                const href = el.getAttribute('href');

                if (target && target !== '_self' && target !== window.name) {
                    if (href && !href.startsWith('/') && !href.startsWith(document.location.origin)) {
                        trackCustomEvent(TrackingTypes.OutgoingLink, href, document.location.href);
                    }
                    return;
                }

                if (href) {
                    handleWithRouter(href, event);
                }
                break;
            }
        }
    };


    if (typeof children === "function") {
        return children({ onClick });
    }

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        <div onClick={onClick}>
            {children}
        </div>
    );

});

export function interceptChildLinkClick(onLinkClick: MouseEventHandler): MouseEventHandler {
    return (e) => {
        const target = e.target as HTMLAnchorElement;

        if (!target || target.tagName !== "A") {
            return;
        }

        onLinkClick(e);
    }
}
